/**
 * Pairing by link, so a device is paired by opening something rather than by thumb-typing a
 * 32-character secret.
 *
 * Inherited from LaunchPad, and the reason pairing a phone is bearable at all: the moment it
 * matters is the moment you are holding the phone, which is exactly when typing a random string is
 * worst.
 *
 * **The link carries the API origin as well as the key**, and that is load-bearing here in a way it
 * is not for LaunchPad. This app is not deployed — the Worker runs on the PC behind a Cloudflare
 * quick tunnel, whose hostname changes every time the tunnel restarts. Baking that into the bundle
 * would mean republishing to EAS after every restart and would leave every already-installed copy
 * pointing at a dead host. Carrying it in the link instead means the bundle is published once and
 * re-pairing is how a device follows the tunnel.
 *
 *     https://…/?key=…&api=https://…            the web build
 *     polysoccerbets://pair?key=…&api=https://…  a standalone native build
 *
 * **The parameters are consumed and stripped immediately.** On web that means rewriting the URL
 * with `history.replaceState`, so the key does not sit in browser history, get shared along with
 * the address, or end up in a screenshot of the address bar. On native there is no address bar, but
 * the link can still be in a chat thread — which is a reason to rotate a key after using one, not a
 * reason to skip the feature.
 *
 * In **Expo Go** the app has no scheme of its own — it is a guest inside Expo Go — so a
 * `polysoccerbets://` link will not reach it. Pair once in Settings there; the deep link is for
 * standalone builds. Saying so is better than shipping a link that silently does nothing.
 */
import { api } from '@/api';
import * as Linking from 'expo-linking';
import { Platform } from 'react-native';

interface Pairing {
  key: string;
  /** Where to talk to. Absent means "leave whatever this device already uses". */
  apiBase?: string;
}

/** The pairing parameters from a URL, or null. Tolerates a malformed URL rather than throwing. */
function pairingFrom(url: string | null): Pairing | null {
  if (!url) return null;
  try {
    const parsed = Linking.parse(url);
    const key = parsed.queryParams?.key;
    if (typeof key !== 'string' || !key.trim()) return null;

    const base = parsed.queryParams?.api;
    // Only http(s). A link is an untrusted carrier, and this value becomes the origin every
    // subsequent request — including the ledger — is sent to.
    const apiBase =
      typeof base === 'string' && /^https?:\/\/\S+$/i.test(base.trim()) ? base.trim() : undefined;

    return { key: key.trim(), apiBase };
  } catch {
    return null;
  }
}

/**
 * Pairs from the launch URL if it carries a key. Returns true when it did, so the caller can
 * repaint rather than leaving a paired device looking unpaired until something else re-renders.
 *
 * An explicit `api` write rather than a store one: pairing has to be durable before anything reads
 * it, and the store's `paired` flag is a mirror of that, not the record.
 */
export async function consumePairingLink(): Promise<boolean> {
  const url = Platform.OS === 'web' ? webUrl() : await Linking.getInitialURL();

  const pairing = pairingFrom(url);
  if (!pairing) return false;

  await api.pair(pairing.key, pairing.apiBase);
  strip();
  return true;
}

/**
 * Watches for a pairing link arriving while the app is already open — the common case on native,
 * where tapping a link hands it to a process that is already running rather than starting one.
 */
export function watchPairingLinks(onPaired: () => void): () => void {
  const sub = Linking.addEventListener('url', ({ url }) => {
    const pairing = pairingFrom(url);
    if (!pairing) return;

    void api.pair(pairing.key, pairing.apiBase).then(() => {
      strip();
      onPaired();
    });
  });

  return () => sub.remove();
}

function webUrl(): string | null {
  return typeof window === 'undefined' ? null : window.location.href;
}

/** Takes the pairing parameters back out of the address bar, leaving every other one alone. */
function strip(): void {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return;

  const params = new URLSearchParams(window.location.search);
  if (!params.has('key') && !params.has('api')) return;

  params.delete('key');
  params.delete('api');
  const query = params.toString();
  window.history.replaceState({}, '', window.location.pathname + (query ? `?${query}` : ''));
}
